import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import BaseInput from "../ui/BaseInput";
import { Button } from "../ui";
import ErrorBanner from "../ui/ErrorBanner";
import { VerifyEmailFormData, VerifyEmailErrors } from "../../types/auth";
import { API_URLS, STORAGE_KEYS } from "../../constants";
import { useAuth, useLanguage } from "@/contexts";

const VerifyEmailPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkSession } = useAuth();

  const [formData, setFormData] = useState<VerifyEmailFormData>({
    email: searchParams.get("email") || "",
    code: "",
  });

  const [errors, setErrors] = useState<VerifyEmailErrors>({});
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Skip auth check during verification flow - we're not authenticated yet
  // The verification process itself will authenticate the user

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(
        () => setResendCooldown(resendCooldown - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleChange =
    (field: keyof VerifyEmailFormData) => (value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear field-specific errors when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  const validateForm = (): boolean => {
    const newErrors: VerifyEmailErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = t("emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t("emailInvalid");
    }

    if (!formData.code.trim()) {
      newErrors.code = t("verificationCodeRequired");
    } else if (!/^\d{6}$/.test(formData.code.trim())) {
      newErrors.code = t("verificationCodeInvalid");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {

      const response = await fetch(API_URLS.AUTH.VERIFY_EMAIL, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email.trim(),
          code: formData.code.trim(),
        }),
      });

      const result = await response.json();


      if (response.ok) {
        // Force a single session check to update auth state
        try {
          await checkSession(true); // Force refresh to get new auth state
          // Small delay to ensure context updates propagate
          await new Promise((resolve) => setTimeout(resolve, 500));
        } catch {
          // Silently handle session check errors
        }

        // Redirect based on user flow
        const redirectPath = localStorage.getItem("redirectAfterLogin");
        const isNewUserRegistration = localStorage.getItem(
          STORAGE_KEYS.NEW_USER_REGISTRATION
        );

        // Clean up localStorage
        localStorage.removeItem("redirectAfterLogin");
        localStorage.removeItem(STORAGE_KEYS.NEW_USER_REGISTRATION);


        // Redirect immediately - the cookie is set, auth will update in background
        if (redirectPath) {
          navigate(redirectPath);
        } else if (isNewUserRegistration) {
          navigate("/discover");
        } else {
          navigate("/");
        }
      } else {
        // Handle specific error cases
        if (response.status === 400) {
          if (result.message?.includes("Invalid or expired")) {
            setErrors({
              code: "Invalid or expired code. Please request a new one.",
            });
          } else if (result.fieldErrors) {
            setErrors(result.fieldErrors);
          } else {
            setErrors({
              general: result.message || t("verificationFailedCode"),
            });
          }
        } else if (response.status === 404) {
          setErrors({
            email: "User not found. Please check your email.",
          });
        } else {
          setErrors({
            general: result.message || t("verificationFailedCode"),
          });
        }
      }
    } catch {
      setErrors({
        general: t("networkError"),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!formData.email.trim()) {
      setErrors({ email: t("verificationEmailError") });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setErrors({ email: t("verificationEmailValidError") });
      return;
    }

    setResendLoading(true);
    setErrors({});
    setResendSuccess(false);

    try {
      const response = await fetch(API_URLS.AUTH.RESEND_VERIFICATION, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email.trim(),
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setResendSuccess(true);
        setResendCooldown(60); // 60 second cooldown
        setFormData((prev) => ({ ...prev, code: "" })); // Clear the code field
      } else {
        setErrors({
          general: result.message || t("resendVerificationCodeFailed"),
        });
      }
    } catch {
      setErrors({
        general: t("networkError"),
      });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t("verifyYourEmail")}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t("verificationCodeSentToYourEmailAddress")}
            {formData.email && (
              <span className="block font-medium text-gray-900 mt-1">
                {formData.email}
              </span>
            )}
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {errors.general && (
            <ErrorBanner
              title={t("verificationFailed")}
              message={errors.general}
              type="error"
            />
          )}

          {resendSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm text-center">
              ✅ {t("verificationCodeSentSuccessfully")}
            </div>
          )}

          <div className="space-y-4">
            <BaseInput
              label={t("email")}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange("email")}
              error={errors.email}
              required
              placeholder="your@email.com"
              autoComplete="email"
            />

            <BaseInput
              label={t("sixDigitVerificationCode")}
              name="code"
              type="text"
              value={formData.code}
              onChange={handleChange("code")}
              error={errors.code}
              required
              placeholder="123456"
              maxLength={6}
              pattern="\d{6}"
              autoComplete="one-time-code"
              helperText={t("sixDigitVerificationCodeHelperText")}
            />
          </div>

          <div className="space-y-4">
            <Button
              type="submit"
              loading={loading}
              className="w-full py-3 bg-brand-dark-blue hover:bg-brand-electric-blue text-white font-medium rounded-md"
              disabled={loading}
            >
              {loading ? t("verifying") : t("verifyEmail")}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resendLoading || resendCooldown > 0}
                className={`text-sm font-medium transition-colors duration-200 ${
                  resendLoading || resendCooldown > 0
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-brand-electric-blue hover:text-brand-dark-blue cursor-pointer"
                }`}
              >
                {resendLoading ? (
                  <span className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    <span>{t("sending")}</span>
                  </span>
                ) : resendCooldown > 0 ? (
                  `${t("resendCodeIn")} ${resendCooldown}s`
                ) : (
                  <>{t("resendVerificationCode")}</>
                )}
              </button>
            </div>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              {t("havingTrouble")}{" "}
              <button
                type="button"
                onClick={() => navigate("/auth/signup")}
                className="text-brand-electric-blue hover:text-brand-dark-blue font-medium"
              >
                {t("backToRegistration")}
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
